// People helpers for rows, avatars and the details pane: names from email
// addresses (Drive only knows emails) and stable avatar tints.

import { initialsOf } from "@fg/ui-theme";

export { initialsOf };

/** Case-insensitive email match; false when either side is missing. */
export function sameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
    return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

/** "bijo.varghese@x.com" → "Bijo Varghese". */
export function nameFromEmail(email: string): string {
    const local = email.split("@")[0] ?? email;
    const words = local.split(/[._-]+/).filter(Boolean);
    if (words.length === 0) return email;
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** "bijo.varghese@x.com" → "Bijo V" (first name + last initial). */
export function shortNameFromEmail(email: string): string {
    const words = nameFromEmail(email).split(" ");
    if (words.length < 2) return words[0] ?? email;
    return `${words[0]} ${words[words.length - 1]!.charAt(0)}`;
}

/** Who saved the latest version, for "Today 14:05 · you" / "· Bijo V". */
export function modifierLabel(modifiedBy: string, me: string | undefined): string {
    return sameEmail(modifiedBy, me) ? "you" : shortNameFromEmail(modifiedBy);
}

// Accent-independent pastel tints (the signed-in user uses the accent-derived
// `.suite-avatar` instead), each with a dark-mode pair.
const TONES = [
    "bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-200",
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-200",
    "bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-200",
    "bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-200",
    "bg-violet-100 text-violet-800 dark:bg-violet-950/70 dark:text-violet-200",
    "bg-cyan-100 text-cyan-900 dark:bg-cyan-950/70 dark:text-cyan-200"
];

/** Stable tint for an email address. */
export function avatarToneClass(email: string): string {
    const key = email.toLowerCase();
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    return TONES[Math.abs(h) % TONES.length]!;
}
